import { AxiosInstance } from "axios";
import { UseQueryResult, useQuery } from "react-query";

import { useAxios } from "../../hooks/useAxios";
import { IUser } from "../../types";
import { useAuth } from "../auth";

export interface IFetchClassroomStudentsParams {
  classroom_id?: string;
  onSuccess?: (data: IFetchClassroomStudentsResponse) => void;
  disabled?: boolean;
}

export interface IFetchClassroomStudentsResponse {
  classroom_id: string;
  users: Array<IUser>;
}

interface IFetchGroupsResponse {
  group_id: string;
  users: Array<IUser>;
}

export const fetchClassroomStudentsQuery = (
  axios: AxiosInstance,
  params: IFetchClassroomStudentsParams,
  userId?: string
): (() => Promise<IFetchClassroomStudentsResponse>) => {
  const { classroom_id } = params;

  return async (): Promise<IFetchClassroomStudentsResponse> => {
    return await axios
      .get(`/v1/users?group_id=${classroom_id}`)
      .then((r) => {
        const data = r.data as IFetchGroupsResponse;
        // Filter out the owner teacher
        const users = data.users.filter((user) => user.id !== userId);
        return { data: { classroom_id: data.group_id, users: users } };
      })
      .then((r) => r.data as IFetchClassroomStudentsResponse);
  };
};

/**
 * Fetches a list of users for the classroom
 * @returns
 */
export default function useFetchClassroomStudents(
  params: IFetchClassroomStudentsParams
): UseQueryResult<IFetchClassroomStudentsResponse, unknown> {
  const axios = useAxios();
  const { authUser } = useAuth();

  const { classroom_id, onSuccess, disabled } = params;

  const query = useQuery<IFetchClassroomStudentsResponse>(
    ["classroom-students", { id: classroom_id }],
    fetchClassroomStudentsQuery(axios, params, authUser?.id),
    { enabled: !!classroom_id && !disabled, onSuccess }
  );

  return query;
}
